import { cva, VariantProps } from "class-variance-authority";
import { formatFollowersCount } from "../../../../Helpers/format-helper";
import { cn } from "../../../../lib/utils";
import { ContentChips, randomColorsForContentChips } from "./atoms";
import { ArrowRight, LockKeyhole, SquarePlus } from "lucide-react";
import { Button } from "@/shadcnComponents/ui/button";
import { Dialog, DialogContent } from "../../../../shadcnComponents/ui/dialog";
import { getRandomProfileEmoji } from "../../../../Helpers/helper";

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/shadcnComponents/ui/tooltip";

type StackAvatarsProps = {
  avatarUrls: string[];
  avatarClass?: string;
  totalCount?: number;
  viewLimit?: number;
  toolTipInfo?: string[];
} & VariantProps<typeof StackAvatarsVariants>;

const StackAvatarsVariants = cva("rounded-full border-white bg-white", {
  variants: {
    variant: {
      default: "-ml-3 border-2 size-10",
      small: "-ml-2 border-1 size-7",
    },
  },
  defaultVariants: {
    variant: "default",
  },
});

export const StackAvatars = ({
  avatarUrls,
  totalCount,
  viewLimit = 3,
  variant = "default",
  avatarClass,
  toolTipInfo,
}: StackAvatarsProps) => {
  const avatars = avatarUrls?.slice(0, viewLimit);
  const moreCount = totalCount - avatars?.length;

  return (
    <div className="flex min-w-10">
      {avatars?.map((url, index) => (
        <>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <img
                  key={index}
                  src={url}
                  alt={`avatar-${index + 1}`}
                  className={cn(
                    "shrink-0 ",
                    StackAvatarsVariants({ variant }),
                    avatarClass
                  )}
                  onError={({ currentTarget }) => {
                    currentTarget.onerror = null; // prevents looping
                    currentTarget.src =
                      getRandomProfileEmoji() ||
                      "https://collab.anchors.in/FeatredCreatorBackground.png";
                  }}
                />
              </TooltipTrigger>
              {toolTipInfo?.[index] && (
                <TooltipContent>
                  <p>{toolTipInfo?.[index]}</p>
                </TooltipContent>
              )}
            </Tooltip>
          </TooltipProvider>
        </>
      ))}
      {moreCount > 0 && (
        <div
          className={cn(
            "flex items-center justify-center shrink-0",
            StackAvatarsVariants({ variant })
          )}
        >
          <span className="text-[#171717] font-[Inter] text-xs not-italic font-bold leading-[24px]">{`+${formatFollowersCount(
            moreCount
          )}`}</span>
        </div>
      )}
    </div>
  );
};

export const ContentChipsArray = ({
  ContentArray,
}: {
  ContentArray: string[];
}) => {
  return (
    <div className="flex w-full md:w-80 items-start content-start gap-2.5 flex-wrap">
      {ContentArray?.map((item, index) => {
        return (
          <ContentChips
            key={index}
            content={item}
            className={randomColorsForContentChips[index]}
          />
        );
      })}
    </div>
  );
};

export const NotLoginChapterMessage = ({
  className,
  userCity,
  handleJoinChapter,
}: {
  className?: string;
  userCity?: string;
  handleJoinChapter?: () => void;
}) => {
  return (
    <div
      className={cn(
        "flex w-full md:w-[672px] flex-col items-center gap-8 ",
        className
      )}
    >
      <LockKeyhole />
      <div className="flex flex-col items-center gap-3 self-stretch">
        <h3 className="text-[color:var(--Shade-White,#FFF)] text-center text-lg md:text-3xl not-italic font-bold leading-[normal]">
          Meet Local Creators in {userCity}
        </h3>
        <p className="text-[color:var(--Shade-White,#FFF)] text-center text-xs md:text-sm not-italic font-normal leading-6">
          This is an exclusive space for LinkedIn creators living in {userCity}{" "}
          to find each other. Make yourself visible to other creators in your
          area to start building connections and grow together.
        </p>
      </div>
      <Button
        className="gap-2.5 bg-[#DC2627] uppercase font-mediums text-sm leading-6 "
        variant="destructive"
        onClick={handleJoinChapter}
      >
        <SquarePlus size={18} />
        <span>Join Chapter</span>
      </Button>
    </div>
  );
};

export const DialogPopup = ({
  children,
  open,
  onOpenChange,
}: {
  children: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#171717] text-white border-none">
        {children}
      </DialogContent>
    </Dialog>
  );
};

export const PopupHeader = ({ title, description, logo }) => {
  return (
    <>
      {title && (
        <h1 className="text-center font-[Inter] text-2xl not-italic font-semibold leading-[24px]">
          {title}
        </h1>
      )}
      {logo && (
        <div className="w-full flex justify-center items-center">
          <img src={logo} alt="" className="size-[90px]" />
        </div>
      )}
      {description && (
        <p className="text-[#D7D7D7] text-center font-[Inter] text-sm not-italic font-normal leading-[22px]">
          {description}
        </p>
      )}
    </>
  );
};

export const PopupSubSection = ({ title, description }) => {
  return (
    <>
      <div className="bg-[#BBBBBB1F] rounded-md px-4 py-3 flex flex-col gap-2 w-full">
        {title && (
          <h2 className="text-center font-[Inter] not-italic font-semibold leading-[24px]">
            {title}
          </h2>
        )}
        {description && (
          <p className="text-[#E2E8F0] text-center font-[Inter] text-xs not-italic font-normal leading-[20px]">
            {description}
          </p>
        )}
      </div>
    </>
  );
};

export const LocPerPopupContent = ({ onRetry }) => {
  return (
    <div className="flex flex-col gap-6 justify-center items-center">
      <PopupHeader
        title={"Location Access Needed!"}
        description={
          "To connect you with creators in your city, we need location access. Please enable it in your browser settings to join the chapter."
        }
        logo={
          "https://anchors-collab.s3.ap-south-1.amazonaws.com/assets/location-pin.webp"
        }
      />

      <div className="">
        <Button
          variant="destructive"
          size="sm"
          className="px-8 items-center gap-2"
          onClick={onRetry}
        >
          <span>Got it!</span>
        </Button>
      </div>
    </div>
  );
};

export const NoChapAvailablePopup = ({ onClickExplore }) => {
  return (
    <div className="flex flex-col gap-6 justify-center items-center">
      <PopupHeader
        title={"No Chapter for Your City… Yet!"}
        description={
          <p>
            We don’t have a chapter in your city right now. Register now, and
            we’ll notify you when it’s live. Meanwhile, explore all that
            <b> anchors </b>
            has to offer!
          </p>
        }
        logo={
          "https://anchors-collab.s3.ap-south-1.amazonaws.com/assets/EmptyBox.png"
        }
      />
      <PopupSubSection
        title={"Connect, Collaborate, Create"}
        description={
          <p>
            Join a supportive creators’ community, score brand partnerships,
            discover what resonates with your audience, & build your media
            kit—all in one place. We’ll keep you posted when a chapter launches
            for your city.
          </p>
        }
      />
      <div>
        <Button
          variant="destructive"
          size="sm"
          className="px-5 items-center gap-2"
          onClick={onClickExplore}
        >
          <span>Explore Now</span>
          <ArrowRight size={14} />
        </Button>
      </div>
    </div>
  );
};

export const NotEligiblePopup = ({
  chapterTitle,
  suggestedChapterTitle,
  handleViewSuggestedChapter,
}) => {
  return (
    <div className="flex flex-col gap-6 justify-center items-center">
      <PopupHeader
        title={"Uh-Oh! Wrong Chapter"}
        description={
          <p>
            You're viewing the <b>{chapterTitle}</b>, but it looks like the
            <b> {suggestedChapterTitle}</b> is your match. Join to connect with
            creators from your city!
          </p>
        }
        logo={
          "https://anchors-collab.s3.ap-south-1.amazonaws.com/assets/OOPS.png"
        }
      />

      <div>
        <Button
          variant="destructive"
          size="sm"
          className="px-5 items-center gap-2"
          onClick={handleViewSuggestedChapter}
        >
          <span>Join {suggestedChapterTitle}</span>
          <ArrowRight size={14} />
        </Button>
      </div>
    </div>
  );
};

export const JoinSuccessPopup = ({ chapterTitle, onClickExplore }) => {
  return (
    <div className="flex flex-col gap-6 justify-center items-center">
      <PopupHeader
        title={"Congrats! You’re In!"}
        description={
          <p>
            Welcome to the <b>{chapterTitle}</b>! Connect with fellow creators
            and build genuine bonds—each connection brings you one step closer
            to achieving your goals.
          </p>
        }
        logo={
          "https://anchors-collab.s3.ap-south-1.amazonaws.com/assets/partyPopper.png"
        }
      />
      <PopupSubSection
        title={"Did You Know?"}
        description={
          <p>
            With <b>anchors</b>, you can gain brand collaborations, deep
            insights into your LinkedIn audience & profile, a custom media kit,
            and more—all in one place, to boost your journey as a content
            creator.
          </p>
        }
      />
      <div>
        <Button
          variant="destructive"
          size="sm"
          className="px-5 items-center gap-2"
          onClick={onClickExplore}
        >
          <span>Explore Now</span>
          <ArrowRight size={14} />
        </Button>
      </div>
    </div>
  );
};
